import { useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, TrendingDown, ArrowRight, AlertCircle, Banknote, Calendar,
  Clock, CheckCircle2, ChevronRight, ArrowDown, ArrowUp,
} from "lucide-react";
import { Button } from "../../components/ui/button";

// ─────────────────────────────────────────────────────────────────────────────
// PREVIEW: Avg-DSO fix + sticky cashflow forecast strip
// Route: /preview/cashflow-strip
// Public, non-destructive — shows the proposed strip in 3 placement options
// so the user can pick before I wire it into /seller dashboard.
// ─────────────────────────────────────────────────────────────────────────────
export default function CashflowStripPreview() {
  const [placement, setPlacement] = useState("under-h1"); // under-h1 | sticky-top | inline-kpis

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center font-bold">T</div>
            <span className="display font-semibold">TRAQO Preview</span>
          </Link>
          <div className="flex items-center gap-1 text-xs">
            {[
              ["under-h1", "Under H1 (banner)"],
              ["sticky-top", "Sticky top-bar"],
              ["inline-kpis", "Inline with KPI tiles"],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setPlacement(v)}
                data-testid={`placement-${v}`}
                className={`px-3 py-1.5 rounded-full border transition-colors ${
                  placement === v
                    ? "bg-foreground text-background border-foreground"
                    : "border-border text-foreground/70 hover:text-foreground"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
          P1 audit fix · Avg-DSO + Cashflow forecast strip
        </div>
        <h1 className="display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] mb-4">
          See your money before it hits the bank.
        </h1>
        <p className="text-foreground/70 max-w-3xl leading-relaxed mb-8">
          Two fixes in one strip: (1) the broken <strong>Avg DSO</strong> calc gets a real number,
          and (2) sellers see a 7- and 30-day cashflow forecast they can act on. Pick a placement
          below and the mock seller dashboard will reflect your choice.
        </p>

        {/* Mock seller dashboard with the chosen placement */}
        <div className="rounded-2xl border border-border bg-background overflow-hidden">
          <MockDashboard placement={placement} />
        </div>

        {/* What's in the strip */}
        <section className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-border p-6">
            <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
              The fix to Avg DSO
            </div>
            <h3 className="display text-xl font-semibold mb-3">
              From <span className="line-through text-foreground/40">0d</span> to a real number
            </h3>
            <p className="text-sm text-foreground/70 leading-relaxed mb-3">
              Today the dashboard shows "AVG DSO 0d" because the calc returns the average over an
              empty filter. The fix:
            </p>
            <ul className="space-y-1.5 text-sm text-foreground/75">
              <li>• Pull all invoices marked <code className="text-xs">paid</code> in the last 90 days</li>
              <li>• For each: <code className="text-xs">paid_at − issued_at</code> in days</li>
              <li>• Average across the set; surface the trend vs prior 90d window</li>
              <li>• Strip shows: "<strong>27d</strong> · 3d faster than last quarter ↓"</li>
            </ul>
          </div>

          <div className="rounded-xl border border-border p-6 bg-accent/[0.05]">
            <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
              The new cashflow strip
            </div>
            <h3 className="display text-xl font-semibold mb-3">
              4 numbers a finance lead checks every morning
            </h3>
            <ul className="space-y-2 text-sm text-foreground/75">
              <li className="flex gap-2"><Banknote className="w-4 h-4 mt-0.5 text-foreground/60 shrink-0" /><span><strong>Today's settled payouts</strong> — what's already in your wallet, ready to withdraw.</span></li>
              <li className="flex gap-2"><Calendar className="w-4 h-4 mt-0.5 text-foreground/60 shrink-0" /><span><strong>Next 7 days</strong> — invoices due to be paid, by buyer score band (high/medium/low confidence).</span></li>
              <li className="flex gap-2"><TrendingUp className="w-4 h-4 mt-0.5 text-foreground/60 shrink-0" /><span><strong>Next 30 days</strong> — same logic, longer horizon, plus expected Allianz claim payouts.</span></li>
              <li className="flex gap-2"><AlertCircle className="w-4 h-4 mt-0.5 text-foreground/60 shrink-0" /><span><strong>At-risk receivables</strong> — invoices whose buyer-score dropped or are 7+ days overdue. Click → filters orders page.</span></li>
            </ul>
          </div>
        </section>

        <section className="mt-8 rounded-xl border border-dashed border-border p-6 bg-foreground/[0.02]">
          <div className="text-xs uppercase tracking-widest text-foreground/50 mb-2">
            Placement trade-offs
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-foreground/75">
            <div>
              <div className="font-medium text-foreground mb-1">Option A · Under H1 (banner)</div>
              <p className="leading-relaxed">Most prominent. Visible only on dashboard. Pushes KPI tiles down ~110px.</p>
              <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-700">Recommended</div>
            </div>
            <div>
              <div className="font-medium text-foreground mb-1">Option B · Sticky top-bar</div>
              <p className="leading-relaxed">Persists across <em>every</em> seller page. Always-on visibility. Costs ~52px of vertical space everywhere.</p>
            </div>
            <div>
              <div className="font-medium text-foreground mb-1">Option C · Inline with KPIs</div>
              <p className="leading-relaxed">Adds 4 cards to the existing KPI row. Cleanest visually but the existing 4 tiles get crowded.</p>
            </div>
          </div>
        </section>

        <div className="mt-12 flex items-center justify-between border-t border-border pt-6">
          <Link to="/" className="text-sm text-foreground/60 hover:text-foreground">← Back to home</Link>
          <span className="text-xs text-foreground/50">Preview only · numbers are mock data</span>
        </div>
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function MockDashboard({ placement }) {
  return (
    <>
      {/* Sticky variant rendered above the H1 */}
      {placement === "sticky-top" && <CashflowStrip variant="sticky" />}

      <div className="px-8 py-6">
        <div className="text-xs uppercase tracking-widest text-foreground/50">/seller</div>
        <h1 className="display text-3xl font-semibold mt-1">Good morning 👋</h1>
        <div className="text-sm text-foreground/55">Here's how GlowCo Beauty is doing today.</div>

        {/* Banner variant rendered between H1 and KPI tiles */}
        {placement === "under-h1" && <CashflowStrip variant="banner" />}

        {/* KPI tiles */}
        <div className={`grid gap-3 mt-6 ${placement === "inline-kpis" ? "grid-cols-4 lg:grid-cols-8" : "grid-cols-4"}`}>
          {[
            { l: "GMV (30d)", v: "$184,220", sub: "+12.4% vs prior" },
            { l: "Open orders", v: "47", sub: "12 awaiting confirm" },
            { l: "Avg DSO", v: "27d", sub: "↓3d vs last Q", tone: "fix" },
            { l: "Outstanding inv.", v: "129", sub: "$94,200 · 4 overdue" },
          ].map((k, i) => (
            <div key={i} className={`rounded-lg border p-3.5 bg-background ${k.tone === "fix" ? "border-foreground bg-accent/5" : "border-border"}`}>
              <div className="text-[10px] uppercase tracking-widest text-foreground/50 flex items-center gap-1">
                {k.l}
                {k.tone === "fix" && <span className="text-[9px] bg-foreground text-background px-1 rounded-full">FIXED</span>}
              </div>
              <div className="display text-2xl font-semibold mt-1">{k.v}</div>
              <div className="text-[11px] text-foreground/55 mt-0.5">{k.sub}</div>
            </div>
          ))}

          {/* Inline-kpis variant adds 4 cashflow cards */}
          {placement === "inline-kpis" && (
            <>
              <CashflowKpi icon={Banknote} label="Settled today" value="$8,420" sub="ready to withdraw" tone="emerald" />
              <CashflowKpi icon={Calendar} label="Due 7d" value="$24,180" sub="92% high confidence" />
              <CashflowKpi icon={TrendingUp} label="Due 30d" value="$94,200" sub="incl. $4.2k Allianz" />
              <CashflowKpi icon={AlertCircle} label="At risk" value="$3,240" sub="2 invoices overdue" tone="rose" />
            </>
          )}
        </div>

        {/* Spacer to make the page look real */}
        <div className="mt-8 grid lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <div key={i} className="rounded-lg border border-border bg-foreground/[0.02] h-40" />
          ))}
        </div>
      </div>
    </>
  );
}

function CashflowStrip({ variant }) {
  return (
    <div className={`${variant === "sticky" ? "border-b" : "rounded-2xl border mt-6"} border-border bg-foreground text-background overflow-hidden`}>
      <div className={`${variant === "sticky" ? "max-w-7xl mx-auto px-8 py-3" : "px-6 py-4"} grid grid-cols-4 items-center gap-3`}>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-background/50 flex items-center gap-1.5">
            <Banknote className="w-3 h-3" />Settled today
          </div>
          <div className="display text-xl font-semibold mt-0.5 flex items-end gap-2">
            $8,420
            <span className="text-[11px] text-emerald-300 font-normal pb-0.5 flex items-center gap-0.5">
              <ArrowUp className="w-3 h-3" />ready to withdraw
            </span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-background/50 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />Due in 7 days
          </div>
          <div className="display text-xl font-semibold mt-0.5 flex items-end gap-2">
            $24,180
            <span className="text-[11px] text-background/55 font-normal pb-0.5">92% high-conf</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-background/50 flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" />Due in 30 days
          </div>
          <div className="display text-xl font-semibold mt-0.5 flex items-end gap-2">
            $94,200
            <span className="text-[11px] text-background/55 font-normal pb-0.5">incl. $4.2k Allianz</span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-rose-300 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3" />At-risk receivables
            </div>
            <div className="display text-xl font-semibold mt-0.5 flex items-end gap-2">
              $3,240
              <span className="text-[11px] text-rose-300 font-normal pb-0.5">2 invoices overdue</span>
            </div>
          </div>
          <Button size="sm" className="rounded-full bg-accent text-foreground hover:bg-accent/90 h-8 px-3 text-xs">
            View <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function CashflowKpi({ icon: Icon, label, value, sub, tone }) {
  const toneCls =
    tone === "emerald" ? "border-emerald-300 bg-emerald-50/50" :
    tone === "rose" ? "border-rose-300 bg-rose-50/50" :
    "border-border bg-background";
  return (
    <div className={`rounded-lg border p-3.5 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-widest text-foreground/50 flex items-center gap-1">
        <Icon className="w-3 h-3" />{label}
      </div>
      <div className="display text-2xl font-semibold mt-1">{value}</div>
      <div className="text-[11px] text-foreground/55 mt-0.5">{sub}</div>
    </div>
  );
}
